import React from 'react';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';

/** Dialog shell shared by the brand and IMSI-series forms.
 *
 * This uses the plain Headless UI v2 API rather than the Transition.Root /
 * Transition.Child pairing the older pages use. That pairing puts `show` on
 * the Transition and `open` on the Dialog, which double-controls the state:
 * the Dialog unmounts before the Transition can run its leave classes, so the
 * exit animation never plays. The panel's visual classes are carried over
 * unchanged, so these modals look identical to the existing ones. */
interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
}

const Modal: React.FC<ModalProps> = ({ open, onClose, title, children, footer }) => (
  <Dialog open={open} onClose={onClose} className="relative z-10">
    <DialogBackdrop
      transition
      className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity duration-300 ease-out data-[closed]:opacity-0"
    />
    <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <DialogPanel
          transition
          className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all duration-300 ease-out data-[closed]:translate-y-4 data-[closed]:opacity-0 sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
        >
          <DialogTitle as="h3" className="text-lg leading-6 font-medium text-gray-900">
            {title}
          </DialogTitle>
          <div className="mt-4 space-y-4">{children}</div>
          {footer && (
            <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
              {footer}
            </div>
          )}
        </DialogPanel>
      </div>
    </div>
  </Dialog>
);

export default Modal;
